import { PropertyValueMap, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { chevronBackIcon, chevronForwardIcon } from '../assets/icons';
import { TmBaseComponent } from '../tm-base.component';

enum Keycode {
    Down = 40,
    Left = 37,
    Right = 39,
    Up = 38,
    Enter = 13,
    Tab = 9,
}

interface TabVm {
    label: string;
    id: number | string;
    disabled?: boolean;
}

export const tagName = 'tm-tabs';
@customElement(tagName)
export class TmTabs extends TmBaseComponent {
    @property({ type: Array })
    public tabs = [] as HTMLElement[] | TabVm[];

    @property()
    public activeTab?: string | number;

    @property({ attribute: false })
    public border: boolean = true;

    @state()
    private isScrollStart = false;

    @state()
    private isScrollEnd = false;

    public get activeTabElements() {
        const tabElements = this.shadowRoot?.querySelectorAll<HTMLElement>('.tab:not([disabled])');
        if (!tabElements) {
            return [];
        }

        return Array.from(tabElements);
    }

    protected firstUpdated(): void {
        const container = this.shadowRoot?.querySelector<HTMLElement>('.scroll-container');
        container?.addEventListener('scroll', () => {
            this.checkScroll();
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    protected updated(changedProperties: PropertyValueMap<any>) {
        if (changedProperties.has('tabs')) {
            this.checkScroll();
        }
    }

    protected render() {
        return html`<div
            class=${classMap({
                'wrapper relative': true,
                'border-b border-[--wrapper-border]': this.border,
            })}
        >
            <div class="scroll-container flex" role="tablist">
                <div class="scroll-content flex">${this.renderTabs()}</div>
            </div>
            ${this.renderScrollIcon('back', this.isScrollStart)}
            ${this.renderScrollIcon('forward', this.isScrollEnd)}
        </div>`;
    }

    private checkScroll() {
        const scrollBuffer = 10;
        const container = this.shadowRoot?.querySelector<HTMLElement>('.scroll-container');
        const content = this.shadowRoot?.querySelector<HTMLElement>('.scroll-content');

        if (!container || !content) {
            throw new Error('Could not check scroll. DOM elements were not found.');
        }

        this.isScrollStart = container.scrollLeft < scrollBuffer;
        this.isScrollEnd =
            content.offsetWidth - container.scrollLeft < container.offsetWidth + scrollBuffer;
    }

    private isActiveTab(id: string | number) {
        return this.activeTab?.toString() === id.toString();
    }

    private handleKeyDown(e: KeyboardEvent) {
        if (e.keyCode === Keycode.Tab) {
            return;
        }

        e.preventDefault();
        const idx = this.activeTabElements.findIndex(
            tab => tab.id === (e.target as HTMLElement).id
        );

        switch (e.keyCode) {
            case Keycode.Down:
            case Keycode.Right:
                this.activeTabElements[(idx + 1) % this.activeTabElements.length].focus();
                break;
            case Keycode.Up:
            case Keycode.Left:
                this.activeTabElements[
                    (idx + this.activeTabElements.length - 1) % this.activeTabElements.length
                ].focus();
                break;
            case Keycode.Enter:
                (e.target as HTMLElement).click();
                break;
            default:
                break;
        }
    }

    private handleTabClick(e: MouseEvent) {
        this.activeTab = (e.target as HTMLElement).id;
        this.dispatchEvent(
            new CustomEvent('switchTab', {
                bubbles: true,
                composed: true,
                detail: { activeTab: this.activeTab },
            })
        );
    }

    private renderScrollIcon(direction: string, isHidden: boolean) {
        return html`<div
            class=${classMap({
                'scroll-indicator absolute top-0 w-6 h-full flex items-center pointer-events-none bg-[--scroll-indicator]': true,
                'left-0': direction === 'back',
                'right-0': direction === 'forward',
                invisible: isHidden,
            })}
        >
            ${direction === 'back' ? chevronBackIcon : chevronForwardIcon}
        </div>`;
    }

    private renderTabs() {
        return this.tabs.map(tab => {
            const disabled = tab instanceof HTMLElement ? false : !!tab.disabled;
            const label = tab instanceof HTMLElement ? '' : tab.label;

            return html`<button
                id=${tab.id}
                class=${classMap({
                    'tab pt-3.5 pb-3 px-4 text-sm whitespace-nowrap': true,
                    'tab-active': this.isActiveTab(tab.id),
                    'font-medium': this.isActiveTab(tab.id),
                    'border-[--tab-border]': this.isActiveTab(tab.id),
                    'border-transparent': !this.isActiveTab(tab.id),
                    'text-[--tab-text]': !this.isActiveTab(tab.id) && disabled,
                    'bg-[--tab-bg-disabled]': disabled,
                    'text-[--tab-text-disabled]': disabled,
                    'cursor-not-allowed': disabled,
                })}
                role="tab"
                aria-selected=${this.isActiveTab(tab.id)}
                ?disabled=${disabled}
                @keydown=${this.handleKeyDown}
                @click=${this.handleTabClick}
            >
                ${label}
            </button>`;
        });
    }

    public static get styles() {
        return [
            css`
                :host {
                    --tab-border: var(--brand-primary);
                    --tab-text: var(--primary-text-color);
                    --tab-text-inactive: var(--secondary-text-color);
                    --tab-text-disabled: var(--jh-color-gray-300);
                    --tab-bg-disabled: rgba(77, 77, 77, 0.031);
                    --scroll-icon: var(--tm-color-gray-medium);
                    --scroll-indicator: rgba(230, 230, 230, 0.698);
                    --wrapper-border: var(--tm-color-gray-light);
                    --item-focus: var(--tm-color-gray-light-30);
                }
                :host([theme='dark']) {
                    --tab-border: var(--jh-color-gray-0);
                    --tab-text: var(--primary-text-color);
                    --tab-text-inactive: var(--secondary-text-color);
                    --tab-text-disabled: var(--tm-color-gray-light-30);
                    --tab-bg-disabled: rgba(255, 255, 255, 0.03);
                    --scroll-icon: var(--tm-color-gray-light);
                    --scroll-indicator: rgba(45, 70, 115, 0.698);
                    --wrapper-border: var(--brand-400);
                    --item-focus: var(--tm-color-gray-light-15);
                }
                .scroll-container {
                    overflow-x: scroll;
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }

                .scroll-container::-webkit-scrollbar {
                    display: none;
                }

                .scroll-indicator path {
                    fill: var(--scroll-icon);
                }

                .tab {
                    border-bottom-width: 3px;
                    color: var(--tab-text-inactive);
                }

                .tab.tab-active {
                    color: var(--tab-text);
                }

                .tab:focus-visible {
                    outline: none;
                    background: var(--item-focus);
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: TmTabs;
    }
}
